import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TransactionsTableRow } from './TransactionTableRow'
import {
  DeviceWidth,
  DeviceWidthCombo,
  useIsDeviceWidth,
} from '@/utils/deviceWidthHelpers'
import { useReselector, useScrollRef, useWindowSize } from '@/utils/sharedHooks'
import {
  filterUserTransactions,
  getAllTransactionFilters,
} from '../transactions.selectors'

import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import {
  TRANSACTIONS_SORT_BY,
  updateTransactionFilters,
} from '@/reducers/finances/transactionFiltersReducer'
import {
  restoreManualTransaction,
  updateUserTransaction,
} from '../transactions.slice'
import { Alert, Button, Card, Table, TableHeaderSort, Text, Loader } from '@/ui'
import { TypographyStyles } from '@/styles'
import { fetchCubkProgress } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/lib/cubkProgress.slice'
import { selectIsBankSyncInProgress } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/lib/cubkProgress.selectors'

import { UserTransactionsFilter } from '../UserTransactionsFilter'
import { getCurrentUser } from '@/selectors/user.selectors'

import { MobileTableRow } from './MobileTableRow'
import { TRANSACTIONS_TABLE_DESCRIPTION } from '../copyConstants'
import { MessageBanner } from './MessageBanner'
import {
  UndoTransactionContext,
  UNDO_TRANSACTION_TYPES,
} from '@/features/transactions/UndoTransactionContext'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UserActionItemActionItemIdentifiers } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { useCompleteOnboardingStepIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import TransactionPagination from '@/features/financial-insights/transactions-panel/TransactionPagination'
import { openPanel } from '@/features/adviser-chat/adviser-chat.slice'

interface TransactionTableProps {
  transactionsType?: 'expenses' | 'income'
  tableType: keyof typeof TRANSACTIONS_TABLE_DESCRIPTION
  fetching: boolean
  /*Indicates if we should allow ability to filter transactions */
  filterable: boolean
  /** When true, hides the date picker + filter toggle row in UserTransactionsFilter */
  hideDateFilterRow?: boolean
}

const TransactionsTable = ({
  transactionsType,
  tableType,
  fetching,
  filterable,
  hideDateFilterRow,
}: TransactionTableProps) => {
  const { scrollRef, scrollToRef } = useScrollRef()
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const [submitting, setSubmitting] = useState(false)
  const [error, showError] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [undoRevertError, setUndoRevertError] = useState(false)
  const [undoMessage, setUndoMessage] = useState('')
  const [searchParams] = useSearchParams()
  const currentUser = useReselector(getCurrentUser)
  const { transactions, count } = useReselector(
    filterUserTransactions,
    tableType
  )
  const isBankSyncInProgress = useReselector(selectIsBankSyncInProgress)
  const { sortVal, sortAsc } = useReselector(getAllTransactionFilters)
  const markComplete = searchParams.get('markComplete')
  const isMobileOrTablet = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const { height } = useWindowSize()

  const onboardingStepCompletion = useCompleteOnboardingStepIfNeeded(
    GettingStartedOnboardingStep.clarifyUncategorizedTransactions
  )

  const {
    setUndoTransactionId,
    setUndoTransactionType,
    undoTransactionId,
    undoTransactionType,
  } = useContext(UndoTransactionContext)

  const incompleteTransactionIds = useMemo(() => {
    if (!showValidationErrors) return new Set<number>()
    return new Set(
      transactions
        .filter((t) => !t.type && !t.transactionCategoryId && !t.notes?.trim())
        .map((t) => t.id)
    )
  }, [transactions, showValidationErrors])

  useEffect(() => {
    if (showValidationErrors && incompleteTransactionIds.size === 0) {
      setShowValidationErrors(false)
    }
  }, [showValidationErrors, incompleteTransactionIds])

  useEffect(() => {
    if (markComplete) {
      markUserActionItemCompleteIfExists(
        UserActionItemActionItemIdentifiers.reviewRecentTransactions,
        (event, properties) => track(event, properties)
      )
    }
    dispatch(fetchFinancialAccountsIfNeeded())
    dispatch(fetchCubkProgress())
  }, [dispatch, markComplete, track])

  const getSorted = (name: string) => {
    return name === sortVal
      ? sortAsc
        ? TableHeaderSort.ASCENDING
        : TableHeaderSort.DESCENDING
      : undefined
  }

  const sortColumn = useCallback(
    (name: string) => {
      if (sortVal === name) {
        dispatch(updateTransactionFilters({ sortAsc: !sortAsc }))
      } else {
        dispatch(updateTransactionFilters({ sortVal: name, sortAsc: false }))
      }
    },
    [dispatch, sortAsc, sortVal]
  )

  const submitReview = async () => {
    const hasIncomplete = transactions.some(
      (t) => !t.type && !t.transactionCategoryId && !t.notes?.trim()
    )
    if (hasIncomplete) {
      setShowValidationErrors(true)
      return
    }

    setShowValidationErrors(false)
    setSubmitting(true)

    try {
      await Promise.all(
        transactions.map((transaction) =>
          updateUserTransaction(transaction.id, {
            requestedClarificationAt: null,
            notifiedRequestedClarificationAt: null,
          })(dispatch)
        )
      )
      markUserActionItemCompleteIfExists(
        UserActionItemActionItemIdentifiers.transactionsNeedReview,
        (event, properties) => track(event, properties)
      )
      onboardingStepCompletion.markComplete()
      setSubmitting(false)
    } catch (_) {
      showError(true)
    }
  }

  const handleUndo = async () => {
    try {
      const isTypeDelete = undoTransactionType === UNDO_TRANSACTION_TYPES.DELETE

      let txn
      if (isTypeDelete) {
        txn = await restoreManualTransaction(undoTransactionId)(dispatch)
      } else {
        txn = await updateUserTransaction(undoTransactionId.toString(), {
          manuallyDedupedAt: null,
        })(dispatch)
      }

      if (!txn) {
        setUndoRevertError(true)
        return
      }

      setUndoTransactionId(NaN)
      setUndoTransactionType('')
      setUndoRevertError(false)
      setUndoMessage('')
    } catch (err) {
      setUndoRevertError(true)
      console.error('Error undoing transaction', err)
    }
  }

  useEffect(() => {
    if (undoTransactionType !== '') {
      const errorMessage = 'Could not undo due to an error. Please try again.'
      const isTypeDelete = undoTransactionType === UNDO_TRANSACTION_TYPES.DELETE
      const message = isTypeDelete
        ? 'Transaction Deleted.'
        : 'Duplicate transaction has been removed.'

      setUndoMessage(undoRevertError ? errorMessage : message)
    }
  }, [undoRevertError, undoTransactionType])

  return (
    <div
      id="transactionsTableContainer"
      className={`
        flex flex-col w-full
        ${!isMobileOrTablet ? 'min-h-[500px]' : ''}
        ${!isMobileOrTablet && height >= 1000 ? 'h-[700px]' : ''}
        ${!isMobileOrTablet && height >= 800 && height < 1000 ? 'h-[600px]' : ''}
        ${!isMobileOrTablet && height >= 600 && height < 800 ? 'h-[500px]' : ''}
        ${!isMobileOrTablet && height < 600 ? 'h-[400px]' : ''}
      `}
    >
      <span ref={scrollRef} />
      <div className="grid grid-cols-1 gap-y-4 sm:gap-y-6">
        {error && (
          <Alert
            variant="error"
            description="It looks like we were unable to save your review! Please refresh and try again."
          />
        )}

        {showValidationErrors && incompleteTransactionIds.size > 0 && (
          <Alert
            variant="error"
            title={`${incompleteTransactionIds.size} transaction${incompleteTransactionIds.size === 1 ? '' : 's'} need${incompleteTransactionIds.size === 1 ? 's' : ''} review`}
            description="Choose business or personal for each, plus a category if it's business. Stuck? Ask Haven or add a note for the bookkeeping team by clicking on the three dots, then Notes & Receipts."
            dismissible
            onClose={() => setShowValidationErrors(false)}
            inlineLinkText="Ask Haven"
            inlineLinkOnClick={() => dispatch(openPanel())}
          />
        )}

        {tableType !== 'all' && tableType !== 'review' && !isMobile && (
          <Alert
            variant="default"
            className="w-full text-center"
            description={TRANSACTIONS_TABLE_DESCRIPTION[tableType]}
          />
        )}

        {count === 0 && (
          <Text variant={TypographyStyles.H3} className="w-full text-center">
            {isBankSyncInProgress
              ? "We're importing your transactions — this can take a few minutes."
              : 'No transactions yet!'}
          </Text>
        )}
      </div>

      {filterable && (
        <UserTransactionsFilter
          transactionsType={transactionsType}
          hideDateFilterRow={hideDateFilterRow}
        />
      )}

      {Boolean(undoTransactionId) && (
        <MessageBanner
          close={() => {
            setUndoTransactionId(NaN)
          }}
          error={undoRevertError}
          message={undoMessage}
          undo={handleUndo}
        />
      )}

      {count !== 0 && (
        <div className="@container overflow-visible">
          {/* Mobile & Tablet View */}
          <div className="block @min-[800px]:hidden space-y-4">
            {transactions.map((transaction) => (
              <MobileTableRow
                key={transaction.id}
                transaction={transaction}
                hasError={incompleteTransactionIds.has(transaction.id)}
              />
            ))}

            {tableType === 'review' && (
              <Card>
                <div className="space-y-3">
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="text-neutral-500"
                  >
                    {TRANSACTIONS_TABLE_DESCRIPTION.review}
                  </Text>
                  <Button onClick={submitReview} disabled={submitting}>
                    Complete review for {transactions.length} transactions
                  </Button>
                </div>
              </Card>
            )}
          </div>

          {/* Desktop View */}
          <div className="hidden @min-[800px]:block overflow-x-auto w-full">
            <Table className="w-full">
              <colgroup>
                <col style={{ width: 40 }} />
                <col style={{ width: 88 }} />
                <col />
                <col style={{ width: 96 }} />
                <col style={{ width: 96 }} />
                <col />
                <col style={{ width: 160 }} />
                <col style={{ width: 40 }} />
              </colgroup>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell />
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.DATE)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.DATE)}
                  >
                    Date
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.DESCRIPTION)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.DESCRIPTION)}
                  >
                    Description
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.AMOUNT)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.AMOUNT)}
                  >
                    Amount
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.TYPE)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.TYPE)}
                  >
                    Type
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.CATEGORY)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.CATEGORY)}
                  >
                    Category
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortable
                    sorted={getSorted(TRANSACTIONS_SORT_BY.ACCOUNT)}
                    onClick={() => sortColumn(TRANSACTIONS_SORT_BY.ACCOUNT)}
                  >
                    Account
                  </Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {!fetching &&
                  currentUser &&
                  transactions.map((transaction) => (
                    <TransactionsTableRow
                      transaction={transaction}
                      key={transaction.id}
                      hasError={incompleteTransactionIds.has(transaction.id)}
                    />
                  ))}
                {fetching && (
                  <Table.Row>
                    <Loader />
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {tableType === 'review' && (
              <Card className="mt-4">
                <div className="flex items-center justify-between gap-4">
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="text-neutral-500"
                  >
                    {TRANSACTIONS_TABLE_DESCRIPTION.review}
                  </Text>
                  <Button
                    onClick={submitReview}
                    disabled={submitting}
                    className="shrink-0"
                  >
                    Complete review for {transactions.length} transactions
                  </Button>
                </div>
              </Card>
            )}
          </div>

          {/* Pagination (Shared) */}
          <div className="mt-6 flex justify-center lg:mt-8 mb-4">
            <TransactionPagination
              transactionsCount={count}
              scrollToRef={scrollToRef}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TransactionsTable
